import { useFormContext, Controller } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Consultation, AssessmentStatus, HistoryConsultation } from "@/types/consultation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { useGetPostureAnalysisQuery } from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { HistoryFilter } from "../history-filter";

const assessmentItems = [
  { title: "Overhead Squat – Anterior View", key: "overhead_squat_anterior" },
  { title: "Overhead Squat – Lateral View", key: "overhead_squat_lateral" },
  { title: "Single Leg Squat – Anterior View", key: "single_leg_anterior" },
  { title: "Pulling & Pushing – Lateral View", key: "pulling_pushing_lateral" },
] as const;

const statusOptions: AssessmentStatus[] = ["Correct", "Not Correct", "Not Applicable"];

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const PostureAnalysisStep = ({ action, open }: FormStepProps) => {
  const { control, watch, setValue } = useFormContext<Consultation>();
  const consultationId = watch("id");
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Fetch existing posture analysis whenever consultationId is available
  const { data: analysisData } = useGetPostureAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: !consultationId || !open }
  );

  // Populate form when data is fetched
  useEffect(() => {
    if (analysisData?.data && Array.isArray(analysisData.data) && analysisData.data.length > 0) {
      const data = analysisData.data[0]; // Access the first item in the array
      
      // Iterate over assessment items and set values if they exist in the data object
      assessmentItems.forEach((item) => {
        const value = (data as any)[item.key];
        
        if (value) {
          setValue(`posture_analysis.${item.key}` as any, value, { shouldDirty: false });
        }
      });
    }
  }, [analysisData, setValue]);

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);
  const memberId = watch("member_id");

  return (
    <div className="space-y-4">
      {/* <HistoryFilter 
        memberId={memberId!} 
        onDataFound={setHistoryData} 
      /> */}

      {historyData && historyData.posture_analysis && (
        <Card className="p-4 border-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
             <p className="font-semibold text-sm text-[#1e293b]">Previous Posture Analysis</p>
             <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                Historical View
             </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
             {assessmentItems.map((item) => (
                <div key={item.key} className="flex justify-between items-center bg-[#f8fafc] border border-[#f1f5f9] p-2 rounded shadow-sm text-xs">
                   <span className="font-medium text-[#64748b]">{item.title}</span>
                   <span className={cn(
                      "font-semibold px-2 py-0.5 rounded",
                      (historyData.posture_analysis as any)[item.key] === "Correct" ? "bg-green-50 text-green-700" :
                      (historyData.posture_analysis as any)[item.key] === "Not Correct" ? "bg-red-50 text-red-700" :
                      "bg-gray-50 text-gray-700"
                   )}>
                      {(historyData.posture_analysis as any)[item.key] || "N/A"}
                   </span>
                </div>
             ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="mb-4">
          <p className="font-medium text-sm">Posture Analysis</p>
          <p className="text-xs text-gray-500 mt-1">
            Complete the following postural assessments for the member.
          </p>
        </div>
        <Separator className="h-[1px] mb-2" />

        <div className="space-y-4">
          {assessmentItems.map((item) => (
            <div key={item.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-[#1e293b]">
                  {item.title}
                </label>
              </div>
              <div className="w-full sm:w-64">
                <Controller
                  control={control}
                  name={`posture_analysis.${item.key}`}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <SelectTrigger className="bg-white border-gray-200 focus:ring-0 rounded-md shadow-sm">
                        <SelectValue placeholder="Select Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {statusOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default PostureAnalysisStep;
